import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Select,
  Input,
  Button,
  Card,
  Row,
  Col,
  Table,
  Checkbox,
  message,

  Divider,
  Typography
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { EmployeeAsset } from '../types/assetTransfer';


const { TextArea } = Input;
const { Title } = Typography;
const { Search } = Input;

const AssetTransferCreatePage: React.FC = () => {
  const navigate = useNavigate();
  
  // Helper function
  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };
  
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser.role === 'super_admin';
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [employeeAssets, setEmployeeAssets] = useState<EmployeeAsset[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);
  const [fromEmployeeId, setFromEmployeeId] = useState<number | null>(null);
  
  // Thêm state cho tìm kiếm và phân trang
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [currentCompanyName, setCurrentCompanyName] = useState<string>('');

  // Filtered assets based on search text
  const filteredAssets = useMemo(() => {
    if (!searchText.trim()) {
      return employeeAssets;
    }
    
    const searchLower = searchText.toLowerCase();
    return employeeAssets.filter(asset => 
      asset.asset_code?.toLowerCase().includes(searchLower) ||
      asset.name?.toLowerCase().includes(searchLower) ||
      asset.category_name?.toLowerCase().includes(searchLower) ||
      asset.location_name?.toLowerCase().includes(searchLower)
    );
  }, [employeeAssets, searchText]);

  // Paginated assets
  const paginatedAssets = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAssets.slice(startIndex, endIndex);
  }, [filteredAssets, currentPage, pageSize]);

  // Load employees by company
  const loadEmployees = async (companyId?: number) => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const response = await apiClient.getEmployees(params) as any;
      
      // Fix: response có thể là array trực tiếp, không cần .data
      const employeesData = Array.isArray(response) ? response : (response.data || []);
      setEmployees(employeesData);
    } catch (error: any) {
      
      message.error('Lỗi khi tải danh sách nhân viên: ' + error.message);
    }
  };

  // Load companies (for superadmin)
  const loadCompanies = async () => {
    try {
      const response = await apiClient.getCompanies() as any;
      
      // Fix: response có thể là array trực tiếp, không cần .data
      const companiesData = Array.isArray(response) ? response : (response.data || []);
      setCompanies(companiesData);
    } catch (error: any) {
      
      message.error('Lỗi khi tải danh sách công ty: ' + error.message);
    }
  };

    

  // Load employee assets
  const loadEmployeeAssets = async (employeeId: number) => {
    try {
      const response = await apiClient.getEmployeeAssets(employeeId) as any;
      setEmployeeAssets(response.data || []);
      // Reset search and pagination when loading new assets
      setSearchText('');
      setCurrentPage(1);
    } catch (error: any) {
      message.error('Lỗi khi tải tài sản của nhân viên: ' + error.message);
    }
  };

  // Load company name for current user
  const loadCurrentCompanyName = async (companyId: number) => {
    try {
      const response = await apiClient.getCompany(companyId) as any;
      setCurrentCompanyName(response.name || 'Công ty hiện tại');
    } catch (error: any) {
      setCurrentCompanyName('Công ty hiện tại');
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      // Super admin cần load tất cả companies để chọn
    loadCompanies();
    } else {
      // Company admin/manager tự động set company của họ
      if (currentUser.company_id) {
        form.setFieldsValue({ company_id: currentUser.company_id });
        loadEmployees(currentUser.company_id);
        loadCurrentCompanyName(currentUser.company_id);
      }
    }
  }, [isSuperAdmin, currentUser.company_id]); // form dependency will be stable after first render

  const handleCompanyChange = (companyId: number) => {
    
    loadEmployees(companyId);
    // Reset form fields when company changes
    form.setFieldsValue({
      from_employee_id: undefined,
      to_employee_id: undefined,
    });
    setFromEmployeeId(null);
    setSelectedAssets([]);
    setEmployeeAssets([]);
  };

  const handleFromEmployeeChange = (employeeId: number) => {
    
    setFromEmployeeId(employeeId);
    setSelectedAssets([]); // Reset selected assets when employee changes
    if (employeeId) {
      loadEmployeeAssets(employeeId);
    } else {
      setEmployeeAssets([]);
    }
  };

  const handleAssetSelection = (assetId: number, checked: boolean) => {
    if (checked) {
      setSelectedAssets(prev => [...prev, assetId]);
    } else {
      setSelectedAssets(prev => prev.filter(id => id !== assetId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAssets(paginatedAssets.map(asset => asset.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSubmit = async (values: any) => {
    if (selectedAssets.length === 0) {
      message.error('Vui lòng chọn ít nhất một tài sản để điều chuyển');
      return;
    }

    setLoading(true);
    try {
      const transferData = {
        ...values,
        asset_ids: selectedAssets,
      };

      
      await apiClient.createAssetTransfer(transferData);
      message.success('Tạo phiếu điều chuyển thành công!');
      navigate('/asset-transfers');
    } catch (error: any) {
      
      
      // Lấy thông báo lỗi từ response của backend
      let errorMessage = 'Lỗi khi tạo phiếu điều chuyển';
      
      if (error.response?.data?.message) {
        // Lấy thông báo từ backend
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Fallback to error.message
        errorMessage = error.message;
      }
      
      // Chuyển đổi **text** thành <strong>text</strong> để làm nổi bật
      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Sử dụng dangerouslySetInnerHTML để hiển thị HTML
      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle pagination change
  const handlePaginationChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const assetColumns = [
    {
      title: (
        <Checkbox
          checked={paginatedAssets.length > 0 && paginatedAssets.every(asset => selectedAssets.includes(asset.id))}
          indeterminate={paginatedAssets.some(asset => selectedAssets.includes(asset.id)) && !paginatedAssets.every(asset => selectedAssets.includes(asset.id))}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'selection',
      width: 50,
      render: (text: any, record: EmployeeAsset) => (
        <Checkbox
          checked={selectedAssets.includes(record.id)}
          onChange={(e) => handleAssetSelection(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 120,
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
    },
    {
      title: 'Vị trí',
      dataIndex: 'location_name',
      key: 'location_name',
      width: 150,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusMap: { [key: string]: string } = {
          'in_use': 'Đang sử dụng',
          'in_storage': 'Lưu kho',
          'under_maintenance': 'Đang bảo dưỡng',
          'disposed': 'Đã thanh lý',
          'lost': 'Mất',
          'damaged': 'Hỏng',
          'pending_deployment': 'Chờ triển khai'
        };
        return statusMap[status] || status;
      },
    },
    {
      title: 'Tình trạng kỹ thuật',
      dataIndex: 'technical_condition',
      key: 'technical_condition',
      width: 150,
      render: (condition: string) => {
        const conditionMap: { [key: string]: string } = {
          'new': 'Mới',
          'good': 'Tốt',
          'fair': 'Khá',
          'poor': 'Kém',
          'bad': 'Hỏng',
          'average': 'Trung bình'
        };
        return conditionMap[condition] || condition;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/asset-transfers')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          Tạo phiếu điều chuyển tài sản
        </Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          reason: '',
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Thông tin điều chuyển" style={{ marginBottom: 24 }}>
                <Form.Item
                  name="company_id"
                  label="Công ty"
                  rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}
                >
                  <Select
                    placeholder="Chọn công ty"
                    showSearch
                    optionFilterProp="children"
                  loading={isSuperAdmin ? companies.length === 0 : false}
                    key={`companies-${companies.length}`}
                    onChange={handleCompanyChange}
                  disabled={!isSuperAdmin}
                  >
                  {isSuperAdmin ? (
                    companies.map(company => (
                      <Select.Option key={company.id} value={company.id}>
                        {company.name}
                      </Select.Option>
                    ))
                  ) : (
                    <Select.Option key={currentUser.company_id} value={currentUser.company_id}>
                      {currentCompanyName || 'Công ty hiện tại'}
                    </Select.Option>
                  )}
                  </Select>
                </Form.Item>

              <Form.Item
                name="from_employee_id"
                label="Nhân viên bàn giao"
                rules={[{ required: true, message: 'Vui lòng chọn nhân viên bàn giao' }]}
              >
                <Select
                  placeholder="Chọn nhân viên bàn giao"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleFromEmployeeChange}
                >
                  {employees.map(employee => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {employee.full_name}{employee.employee_code ? ` (${employee.employee_code})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="to_employee_id"
                label="Nhân viên nhận bàn giao"
                rules={[{ required: true, message: 'Vui lòng chọn nhân viên nhận' }]}
              >
                <Select
                  placeholder="Chọn nhân viên nhận bàn giao"
                  showSearch
                  optionFilterProp="children"
                >
                  {employees.map(employee => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {employee.full_name}{employee.employee_code ? ` (${employee.employee_code})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="reason"
                label="Lý do điều chuyển"
                rules={[{ required: true, message: 'Vui lòng nhập lý do điều chuyển' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Nhập lý do điều chuyển tài sản..."
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Thống kê" style={{ marginBottom: 24 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {selectedAssets.length}
                </div>
                <div style={{ color: '#666' }}>Tài sản được chọn</div>
              </div>
              
              <Divider />
              
              <div style={{ fontSize: '14px', color: '#666' }}>
                <div>Nhân viên bàn giao: {fromEmployeeId ? employees.find(e => e.id === fromEmployeeId)?.full_name : '—'}</div>
                <div>Tổng tài sản: {employeeAssets.length}</div>
                {searchText && (
                  <div>Tìm thấy: {filteredAssets.length} tài sản</div>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        <Card 
          title="Danh sách tài sản của nhân viên bàn giao"
          extra={
            fromEmployeeId && (
              <Search
                placeholder="Tìm kiếm tài sản..."
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                style={{ width: 300 }}
                onSearch={handleSearch}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />
            )
          }
        >
          {fromEmployeeId ? (
            <Table
              columns={assetColumns}
              dataSource={paginatedAssets}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredAssets.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} tài sản`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: handlePaginationChange,
                onShowSizeChange: handlePaginationChange,
              }}
              scroll={{ x: 1000 }}
              size="small"
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Vui lòng chọn nhân viên bàn giao để xem danh sách tài sản
            </div>
          )}
        </Card>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={() => form.submit()}
            disabled={selectedAssets.length === 0}
          >
            Tạo phiếu điều chuyển
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AssetTransferCreatePage; 
